import type { RootState } from '@ui/app/store';
import type { PHIBoundingBox } from '@client/features/DeIdentification/types';
import type { TextFile } from '@client/features/DeIdentification/data/mockTextFiles';

// Base selector
export const selectImagingViewer = (state: RootState) => state.imagingViewer;

// Individual state selectors
export const selectImagingStudies = (state: RootState) => state.imagingViewer.imagingStudies;
export const selectSelectedStudyId = (state: RootState) => state.imagingViewer.selectedStudyId;
export const selectIsLoadingStudies = (state: RootState) => state.imagingViewer.isLoadingStudies;
export const selectIsLoadingDeidStudies = (state: RootState) => state.imagingViewer.isLoadingDeidStudies;
export const selectIsLoadingSeries = (state: RootState) => state.imagingViewer.isLoadingSeries;
export const selectIsLoadingInstances = (state: RootState) => state.imagingViewer.isLoadingInstances;
export const selectIsLoadingDeidSeries = (state: RootState) => state.imagingViewer.isLoadingDeidSeries;
export const selectIsLoadingDeidInstances = (state: RootState) => state.imagingViewer.isLoadingDeidInstances;
export const selectSlices = (state: RootState) => state.imagingViewer.slices;
export const selectAnonymizedSlices = (state: RootState) => state.imagingViewer.anonymizedSlices;
export const selectFilteredOriginalSlices = (state: RootState) => state.imagingViewer.filteredOriginalSlices;
export const selectFilteredDeidSlices = (state: RootState) => state.imagingViewer.filteredDeidSlices;
export const selectCurrentSliceIndex = (state: RootState) => state.imagingViewer.currentSliceIndex;
export const selectAllBoundingBoxes = (state: RootState) => state.imagingViewer.allBoundingBoxes;
export const selectMaskedBoundingBoxes = (state: RootState) => state.imagingViewer.maskedBoundingBoxes;
export const selectOriginalMetadata = (state: RootState) => state.imagingViewer.originalMetadata;
export const selectAnonymizedMetadata = (state: RootState) => state.imagingViewer.anonymizedMetadata;
export const selectIsLoadingMetadata = (state: RootState) => state.imagingViewer.isLoadingMetadata ?? false;
export const selectPhiTagSet = (state: RootState) => state.imagingViewer.phiTagSet;
export const selectSelectedProfile = (state: RootState) => state.imagingViewer.selectedProfile;
export const selectIsDualDatastoreComparison = (state: RootState) => state.imagingViewer.isDualDatastoreComparison;
export const selectOriginalItems = (state: RootState) => state.imagingViewer.originalItems;
export const selectDeidItems = (state: RootState) => state.imagingViewer.deidItems;
export const selectSelectedOriginalSliceId = (state: RootState) => state.imagingViewer.selectedOriginalSliceId;
export const selectSelectedDeidSliceId = (state: RootState) => state.imagingViewer.selectedDeidSliceId;
export const selectDeIDResultId = (state: RootState) => state.imagingViewer.deIDResultId;
export const selectDeIDResultStatus = (state: RootState) => state.imagingViewer.deIDResultStatus;
export const selectDeIDResult = (state: RootState) => state.imagingViewer.deIDResult;
export const selectPipelineResult = (state: RootState) => state.imagingViewer.pipelineResult;
export const selectFirstSeriesId = (state: RootState) => state.imagingViewer.firstSeriesId;
export const selectMetadataDecisions = (state: RootState) => state.imagingViewer.metadataDecisions;
export const selectPhiDecisions = (state: RootState) => state.imagingViewer.phiDecisions;
export const selectIsWaitingForDicomOnDeid018 = (state: RootState) => state.imagingViewer.isWaitingForDicomOnDeid018;
export const selectSelectedBoundingBox = (state: RootState) => state.imagingViewer.selectedBoundingBox;
export const selectDecisionPanelVisible = (state: RootState) => state.imagingViewer.decisionPanelVisible;

// Computed selectors
export const selectIsViewerLoading = (state: RootState): boolean => {
    const {
        isLoadingSeries,
        isLoadingInstances,
        isLoadingDeidSeries,
        isLoadingDeidInstances,
        isDualDatastoreComparison,
    } = state.imagingViewer;
    return (
        isLoadingSeries ||
        isLoadingInstances ||
        (isDualDatastoreComparison && (isLoadingDeidSeries || isLoadingDeidInstances))
    );
};

export const selectHasSlices = (state: RootState): boolean => {
    const { filteredOriginalSlices, filteredDeidSlices } = state.imagingViewer;
    return filteredOriginalSlices.length > 0 || filteredDeidSlices.length > 0;
};

export const selectShouldShowLoading = (state: RootState): boolean => {
    const { selectedStudyId, isWaitingForDicomOnDeid018 } = state.imagingViewer;
    const isViewerLoading = selectIsViewerLoading(state);
    const hasSlices = selectHasSlices(state);
    return isViewerLoading || (selectedStudyId && !hasSlices) || (isWaitingForDicomOnDeid018 ?? false);
};

export const selectShouldShowEmptyState = (state: RootState): boolean => {
    const { selectedStudyId, isWaitingForDicomOnDeid018 } = state.imagingViewer;
    const isViewerLoading = selectIsViewerLoading(state);
    const hasSlices = selectHasSlices(state);
    const shouldShowLoading = selectShouldShowLoading(state);
    return (
        !!selectedStudyId &&
        !isViewerLoading &&
        !hasSlices &&
        !(isWaitingForDicomOnDeid018 ?? false) &&
        !shouldShowLoading
    );
};

export const selectFilteredBoundingBoxes = (state: RootState): PHIBoundingBox[] => {
    const { allBoundingBoxes, filteredOriginalSlices } = state.imagingViewer;
    return allBoundingBoxes.filter(bbox => filteredOriginalSlices.some(s => s.slice_id === bbox.slice_id));
};

// Text files and tab selectors
export const selectTextFiles = (state: RootState): TextFile[] => state.imagingViewer.textFiles;
export const selectSelectedTextFileId = (state: RootState): string | undefined =>
    state.imagingViewer.selectedTextFileId;
export const selectActiveTab = (state: RootState): 'imaging' | 'text' => state.imagingViewer.activeTab;
export const selectHideImagingTab = (state: RootState): boolean => state.imagingViewer.hideImagingTab;
export const selectStrictDeidMetadata = (state: RootState): boolean => state.imagingViewer.strictDeidMetadata;
export const selectIsDeidLoading = (state: RootState): boolean => state.imagingViewer.isDeidLoading;
export const selectShowExport = (state: RootState): boolean => state.imagingViewer.showExport;
